import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Download, FileText, IndianRupee, Loader2, AlertCircle } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getFYOptions() {
  const now = new Date();
  const currentFYStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const options = [];
  for (let y = currentFYStart; y >= currentFYStart - 2; y--) {
    options.push({ value: `${y}-${String(y + 1).slice(2)}`, label: `FY ${y}-${String(y + 1).slice(2)}` });
  }
  return options;
}

export default function TDSReports() {
  const { token } = useAuth();
  const fyOptions = getFYOptions();
  const [selectedFY, setSelectedFY] = useState(fyOptions[0]?.value || '');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const fetchReport = useCallback(async () => {
    if (!token || !selectedFY) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/payroll/tds-report?fy=${selectedFY}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setReport(await res.json());
    } catch (err) {
      console.error('TDS report fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, selectedFY]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/payroll/tds-report/download?fy=${selectedFY}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `TDS_Report_FY${selectedFY}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloading(false);
    }
  };

  const monthLabels = report?.month_labels || [];
  const employees = report?.employees || [];

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto" data-testid="tds-reports-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">TDS Reports</h1>
            <p className="text-sm text-slate-500 mt-0.5">Monthly TDS deduction breakdown per employee</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedFY} onValueChange={setSelectedFY}>
              <SelectTrigger className="w-[160px]" data-testid="tds-fy-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {fyOptions.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={handleDownload}
              disabled={downloading || !employees.length}
              className="gap-2"
              data-testid="tds-download-btn"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download CSV
            </Button>
          </div>
        </div>

        {/* Summary Card */}
        {report && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card className="border-slate-200/60 dark:border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
                    <IndianRupee className="w-4 h-4 text-red-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="tds-total-amount">
                  {formatCurrency(report.total_tds, 'INR')}
                </p>
                <p className="text-xs text-slate-400 mt-0.5 uppercase tracking-wider font-medium">Total TDS Deducted (YTD)</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200/60 dark:border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{employees.length}</p>
                <p className="text-xs text-slate-400 mt-0.5 uppercase tracking-wider font-medium">Employees with TDS Data</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200/60 dark:border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
                    <IndianRupee className="w-4 h-4 text-emerald-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {formatCurrency(employees.reduce((s, e) => s + e.total_gross, 0), 'INR')}
                </p>
                <p className="text-xs text-slate-400 mt-0.5 uppercase tracking-wider font-medium">Total Gross (YTD)</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* TDS Table */}
        <Card className="border-slate-200/60 dark:border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Employee-wise TDS Breakdown — FY {selectedFY}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : employees.length === 0 ? (
              <div className="text-center py-16">
                <AlertCircle className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-400">No processed pay runs found for FY {selectedFY}</p>
                <p className="text-xs text-slate-400 mt-1">Process payroll to see TDS data here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="tds-report-table">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                      <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-slate-500 sticky left-0 bg-slate-50 dark:bg-slate-800/50 z-10">Employee</th>
                      <th className="text-left px-3 py-3 font-semibold text-xs uppercase tracking-wider text-slate-500">PAN</th>
                      {monthLabels.map(ml => (
                        <th key={ml.key} className="text-right px-3 py-3 font-semibold text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap">
                          {ml.label}
                        </th>
                      ))}
                      <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider text-slate-900 dark:text-white bg-slate-100 dark:bg-slate-700/50">Total TDS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((emp, idx) => (
                      <tr key={emp.user_id} className={`border-b border-slate-100 dark:border-slate-700/50 ${idx % 2 === 0 ? '' : 'bg-slate-50/50 dark:bg-slate-800/30'}`}>
                        <td className="px-4 py-3 font-medium text-slate-900 dark:text-white sticky left-0 bg-white dark:bg-slate-900 z-10">
                          <div>{emp.employee_name}</div>
                          {emp.employee_id && <div className="text-xs text-slate-400">{emp.employee_id}</div>}
                        </td>
                        <td className="px-3 py-3 text-slate-500 font-mono text-xs">{emp.pan || '—'}</td>
                        {monthLabels.map(ml => {
                          const md = emp.months[ml.key];
                          return (
                            <td key={ml.key} className="text-right px-3 py-3 tabular-nums">
                              {md && md.tds > 0 ? (
                                <span className="text-red-600 dark:text-red-400 font-medium">{formatCurrency(md.tds, 'INR')}</span>
                              ) : (
                                <span className="text-slate-300 dark:text-slate-600">—</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="text-right px-4 py-3 font-bold text-slate-900 dark:text-white bg-slate-50 dark:bg-slate-800/50 tabular-nums">
                          {emp.total_tds > 0 ? formatCurrency(emp.total_tds, 'INR') : '—'}
                        </td>
                      </tr>
                    ))}
                    {/* Totals row */}
                    <tr className="border-t-2 border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 font-bold">
                      <td className="px-4 py-3 text-slate-900 dark:text-white sticky left-0 bg-slate-100 dark:bg-slate-800 z-10" colSpan={2}>
                        Total
                      </td>
                      {monthLabels.map(ml => {
                        const monthTotal = employees.reduce((s, e) => s + (e.months[ml.key]?.tds || 0), 0);
                        return (
                          <td key={ml.key} className="text-right px-3 py-3 tabular-nums text-slate-900 dark:text-white">
                            {monthTotal > 0 ? formatCurrency(monthTotal, 'INR') : '—'}
                          </td>
                        );
                      })}
                      <td className="text-right px-4 py-3 text-slate-900 dark:text-white bg-slate-200 dark:bg-slate-700 tabular-nums">
                        {formatCurrency(report?.total_tds || 0, 'INR')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
